import {Edge, Node} from 'reactflow';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';
import {IQuestionElements} from '../../../../incase-domain/elements/question';
import {ISlidePreview} from '../../../../incase-domain/proptypes/slidePreview';
import {ISlide} from '../../../../incase-domain/slide';
import {QuestionNodeData, SlideNodeData} from '../RulesFlowEditor/nodes.types';
import {
    ANSWER_ROW_HEIGHT,
    QUESTION_GAP,
    QUESTION_HEADER_HEIGHT,
    QUESTION_NODE_WIDTH,
    SLIDE_GAP,
    SLIDE_NODE_HEIGHT,
    SLIDE_NODE_WIDTH,
    VERTICAL_GAP,
} from './flow.constants';
import {LINK_EDGE_STYLE, SEQ_EDGE_STYLE} from './flow.styles';

export const PREDICATEABLE_ELEMENTS: ELEMENT[] = [
    ELEMENT.QUESTION,
    ELEMENT.PHOTO_ANSWER_QUESTION,
    ELEMENT.YES_NO_QUESTION_V2,
    ELEMENT.MULTI_SELECT,
];

function estimateQuestionHeight(el: IQuestionElements): number {
    const answerCount = 'answers' in el ? el.answers?.length ?? 2 : 2;
    return QUESTION_HEADER_HEIGHT + answerCount * ANSWER_ROW_HEIGHT;
}

export function buildStructuralNodes(slides: ISlide[], previews: ISlidePreview[] | undefined): Node[] {
    const nodes: Node[] = [];
    const visibleSlides = slides.filter((s) => !s.hidden);

    visibleSlides.forEach((slide, i) => {
        const slideX = i * (SLIDE_NODE_WIDTH + SLIDE_GAP);
        nodes.push({
            id: `slide-${slide._id}`,
            type: 'slide',
            position: {x: slideX, y: 0},
            data: {slide, preview: previews?.find((p) => p.slideId === slide._id)} as SlideNodeData,
            style: {width: SLIDE_NODE_WIDTH},
            draggable: false,
            selectable: false,
            deletable: false,
        });

        let cumulativeY = SLIDE_NODE_HEIGHT + VERTICAL_GAP;
        const qEls = (slide.elements ?? []).filter((el) => {
            if ('codebookId' in el) return false;
            return PREDICATEABLE_ELEMENTS.includes(el.type as ELEMENT);
        }) as IQuestionElements[];

        qEls.forEach((el) => {
            nodes.push({
                id: `question-${el.id}`,
                type: 'question',
                position: {x: slideX + SLIDE_NODE_WIDTH - QUESTION_NODE_WIDTH, y: cumulativeY},
                data: {element: el, slideId: slide._id} as QuestionNodeData,
                style: {width: QUESTION_NODE_WIDTH},
                draggable: false,
                selectable: false,
                deletable: false,
            });
            cumulativeY += estimateQuestionHeight(el) + QUESTION_GAP;
        });
    });

    return nodes;
}

export function buildStructuralEdges(slides: ISlide[]): Edge[] {
    const edges: Edge[] = [];
    const visibleSlides = slides.filter((s) => !s.hidden);

    visibleSlides.forEach((slide, i) => {
        const next = visibleSlides[i + 1];
        if (next) {
            edges.push({
                id: `seq-${slide._id}-${next._id}`,
                source: `slide-${slide._id}`,
                sourceHandle: 'next-slide',
                target: `slide-${next._id}`,
                targetHandle: 'prev-slide',
                ...SEQ_EDGE_STYLE,
            });
        }
        (slide.elements ?? [])
            .filter((el) => PREDICATEABLE_ELEMENTS.includes(el.type as ELEMENT))
            .forEach((el) => {
                edges.push({
                    id: `struct-${slide._id}-${el.id}`,
                    source: `slide-${slide._id}`,
                    sourceHandle: 'to-questions',
                    target: `question-${el.id}`,
                    targetHandle: 'from-slide',
                    ...LINK_EDGE_STYLE,
                });
            });
    });

    return edges;
}
